import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { API_ENDPOINTS } from '../../config/api';
import './FeedbackModal.css';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const { isDarkMode } = useTheme();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !message.trim()) {
      setNotification({ message: 'Please fill in all fields', type: 'error' });
      return;
    }

    if (!email.includes('@')) {
      setNotification({ message: 'Please enter a valid email address', type: 'error' });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(API_ENDPOINTS.FEEDBACK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          message: message.trim(),
        }),
      });

      if (response.ok) {
        setNotification({ message: 'Thank you for your feedback!', type: 'success' });
        setEmail('');
        setMessage('');
        setTimeout(() => {
          onClose();
          setNotification(null);
        }, 1500);
      } else {
        setNotification({ message: 'Failed to send feedback. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
      setNotification({ message: 'Failed to send feedback. Please try again.', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setEmail('');
      setMessage('');
      setNotification(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="feedback-modal-overlay" onClick={handleClose}>
      <div 
        className={`feedback-modal ${isDarkMode ? 'dark' : 'light'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="feedback-modal-header">
          <h2>Support</h2>
          <button 
            className="feedback-modal-close" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>
        
        {notification && (
          <div className={`feedback-notification ${notification.type}`}>
            {notification.message}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="feedback-form">
          <div className="feedback-form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
              disabled={isSubmitting}
            />
          </div>
          
          <div className="feedback-form-group">
            <label htmlFor="message">Your Feedback</label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what you think, report bugs, or suggest improvements..."
              rows={5}
              required
              disabled={isSubmitting}
            />
          </div>
          
          <div className="feedback-form-actions">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="feedback-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="feedback-submit-btn"
            >
              {isSubmitting ? 'Sending...' : 'Send Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;
